// Theme preset definitions for CommandBridge

export type ThemeMode = 'light' | 'dark' | 'system';
export type FontSize = 'small' | 'medium' | 'large' | 'extra-large';
export type Density = 'compact' | 'comfortable' | 'spacious';
export type BorderRadius = 'sharp' | 'slightly-rounded' | 'rounded' | 'very-rounded';
export type AnimationSpeed = 'none' | 'reduced' | 'normal' | 'enhanced';

export interface ThemeColors {
  primary: string;
  primaryDark: string;
  accent: string;
  accentDark: string;
  sidebar: string;
  sidebarDark: string;
}

export interface CustomTheme {
  id: string;
  name: string;
  colors: ThemeColors;
  mode: ThemeMode;
  fontSize: FontSize;
  density: Density;
  borderRadius: BorderRadius;
  animationSpeed: AnimationSpeed;
}

// Default theme — Command Blue-Teal: authoritative, professional, distinctive
export const defaultTheme: CustomTheme = {
  id: 'default',
  name: 'Command Blue',
  colors: {
    primary: '#2181a8',
    primaryDark: '#0f4260',
    accent: '#2b9ebc',
    accentDark: '#1a7f9e',
    sidebar: '#0e2a3d',
    sidebarDark: '#081c2b',
  },
  // Dark is the product default (console identity); a user's stored theme
  // choice always wins since the theme store persists their selection.
  mode: 'dark',
  fontSize: 'medium',
  density: 'comfortable',
  borderRadius: 'rounded',
  animationSpeed: 'normal',
};

// Preset themes
export const themePresets: CustomTheme[] = [
  defaultTheme,
  {
    id: 'emergency-red',
    name: 'Emergency Red',
    colors: {
      primary: '#dc2626',
      primaryDark: '#991b1b',
      accent: '#f97316',
      accentDark: '#c2410c',
      sidebar: '#1f2937',
      sidebarDark: '#111827',
    },
    mode: 'system',
    fontSize: 'medium',
    density: 'comfortable',
    borderRadius: 'rounded',
    animationSpeed: 'normal',
  },
  {
    id: 'forest-green',
    name: 'Forest Green',
    colors: {
      primary: '#059669',
      primaryDark: '#065f46',
      accent: '#10b981',
      accentDark: '#047857',
      sidebar: '#1f2937',
      sidebarDark: '#111827',
    },
    mode: 'system',
    fontSize: 'medium',
    density: 'comfortable',
    borderRadius: 'rounded',
    animationSpeed: 'normal',
  },
  {
    id: 'ocean-blue',
    name: 'Ocean Blue',
    colors: {
      primary: '#0891b2',
      primaryDark: '#155e75',
      accent: '#06b6d4',
      accentDark: '#0e7490',
      sidebar: '#1f2937',
      sidebarDark: '#111827',
    },
    mode: 'system',
    fontSize: 'medium',
    density: 'comfortable',
    borderRadius: 'rounded',
    animationSpeed: 'normal',
  },
  {
    id: 'midnight',
    name: 'Midnight',
    colors: {
      primary: '#7c3aed',
      primaryDark: '#5b21b6',
      accent: '#a78bfa',
      accentDark: '#7c3aed',
      sidebar: '#1e1b4b',
      sidebarDark: '#0f172a',
    },
    mode: 'dark',
    fontSize: 'medium',
    density: 'comfortable',
    borderRadius: 'rounded',
    animationSpeed: 'normal',
  },
  {
    id: 'high-contrast',
    name: 'High Contrast',
    colors: {
      primary: '#000000',
      primaryDark: '#000000',
      accent: '#0066cc',
      accentDark: '#004499',
      sidebar: '#000000',
      sidebarDark: '#000000',
    },
    mode: 'light',
    fontSize: 'large',
    density: 'spacious',
    borderRadius: 'sharp',
    animationSpeed: 'reduced',
  },
];

// Font size mappings (rem values)
export const fontSizeMap: Record<FontSize, string> = {
  'small': '0.875rem',      // 14px
  'medium': '1rem',          // 16px (default)
  'large': '1.125rem',       // 18px
  'extra-large': '1.25rem',  // 20px
};

// Density spacing multipliers
export const densityMap: Record<Density, number> = {
  'compact': 0.75,
  'comfortable': 1,
  'spacious': 1.25,
};

// Border radius values
export const borderRadiusMap: Record<BorderRadius, string> = {
  'sharp': '0px',
  'slightly-rounded': '0.25rem',  // 4px
  'rounded': '0.5rem',             // 8px
  'very-rounded': '1rem',          // 16px
};

// Animation duration multipliers
export const animationSpeedMap: Record<AnimationSpeed, number> = {
  'none': 0,
  'reduced': 0.5,
  'normal': 1,
  'enhanced': 1.5,
};

// Helper function to check color contrast for accessibility
export function getContrastRatio(color1: string, color2: string): number {
  const getLuminance = (hex: string): number => {
    const rgb = parseInt(hex.slice(1), 16);
    const r = ((rgb >> 16) & 0xff) / 255;
    const g = ((rgb >> 8) & 0xff) / 255;
    const b = (rgb & 0xff) / 255;

    const [rs, gs, bs] = [r, g, b].map(c => {
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  };

  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);

  return (lighter + 0.05) / (darker + 0.05);
}

// Check if a color combination meets WCAG AA standards
export function meetsContrastStandard(foreground: string, background: string): boolean {
  const ratio = getContrastRatio(foreground, background);
  return ratio >= 4.5; // WCAG AA for normal text
}

// Export theme as JSON string
export function exportTheme(theme: CustomTheme): string {
  return JSON.stringify(theme, null, 2);
}

// Import theme from JSON string with full validation
export function importTheme(json: string): CustomTheme {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new Error('Failed to import theme: Invalid JSON');
  }

  if (typeof parsed !== 'object' || parsed === null) {
    throw new Error('Failed to import theme: Expected a JSON object');
  }

  const theme = parsed as Record<string, unknown>;

  // Validate required string fields
  if (typeof theme.id !== 'string' || theme.id.length === 0 || theme.id.length > 100) {
    throw new Error('Failed to import theme: Invalid or missing "id"');
  }
  if (typeof theme.name !== 'string' || theme.name.length === 0 || theme.name.length > 100) {
    throw new Error('Failed to import theme: Invalid or missing "name"');
  }

  // Validate enum fields
  const validModes: ThemeMode[] = ['light', 'dark', 'system'];
  if (!validModes.includes(theme.mode as ThemeMode)) {
    throw new Error(`Failed to import theme: Invalid "mode". Expected one of: ${validModes.join(', ')}`);
  }

  const validFontSizes: FontSize[] = ['small', 'medium', 'large', 'extra-large'];
  if (!validFontSizes.includes(theme.fontSize as FontSize)) {
    throw new Error(`Failed to import theme: Invalid "fontSize". Expected one of: ${validFontSizes.join(', ')}`);
  }

  const validDensities: Density[] = ['compact', 'comfortable', 'spacious'];
  if (!validDensities.includes(theme.density as Density)) {
    throw new Error(`Failed to import theme: Invalid "density". Expected one of: ${validDensities.join(', ')}`);
  }

  const validRadii: BorderRadius[] = ['sharp', 'slightly-rounded', 'rounded', 'very-rounded'];
  if (!validRadii.includes(theme.borderRadius as BorderRadius)) {
    throw new Error(`Failed to import theme: Invalid "borderRadius". Expected one of: ${validRadii.join(', ')}`);
  }

  const validSpeeds: AnimationSpeed[] = ['none', 'reduced', 'normal', 'enhanced'];
  if (!validSpeeds.includes(theme.animationSpeed as AnimationSpeed)) {
    throw new Error(`Failed to import theme: Invalid "animationSpeed". Expected one of: ${validSpeeds.join(', ')}`);
  }

  // Validate colors object
  if (typeof theme.colors !== 'object' || theme.colors === null) {
    throw new Error('Failed to import theme: Missing "colors" object');
  }

  const colors = theme.colors as Record<string, unknown>;
  const hexPattern = /^#[0-9A-Fa-f]{6}$/;
  const requiredColors: (keyof ThemeColors)[] = ['primary', 'primaryDark', 'accent', 'accentDark', 'sidebar', 'sidebarDark'];

  for (const colorKey of requiredColors) {
    const val = colors[colorKey];
    if (typeof val !== 'string' || !hexPattern.test(val)) {
      throw new Error(`Failed to import theme: Invalid or missing color "${colorKey}". Expected a hex color like #1a2b3c`);
    }
  }

  // Return only the validated fields (strip any extra properties)
  return {
    id: theme.id as string,
    name: theme.name as string,
    mode: theme.mode as ThemeMode,
    fontSize: theme.fontSize as FontSize,
    density: theme.density as Density,
    borderRadius: theme.borderRadius as BorderRadius,
    animationSpeed: theme.animationSpeed as AnimationSpeed,
    colors: {
      primary: colors.primary as string,
      primaryDark: colors.primaryDark as string,
      accent: colors.accent as string,
      accentDark: colors.accentDark as string,
      sidebar: colors.sidebar as string,
      sidebarDark: colors.sidebarDark as string,
    },
  };
}
